import axios from "axios";
import { adminApi } from "../../lib/axios";

// Types for Affiliate Management
export interface PhoneNumber {
  countryCode: string | null;
  number: string | null;
  fullNumber: string | null;
}

export interface Niche {
  id: string;
  name: string;
  description: string;
  status: "ACTIVE" | "INACTIVE";
  createdAt: string;
  updatedAt: string;
  nicheId: string | null;
}

export interface SocialLink {
  id: string;
  username: string;
  platform?: string;
  iconUrl?: string;
  url?: string;
}

export interface AdminAffiliate {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  country: string | null;
  phoneNumber: PhoneNumber;
  gender: "MALE" | "FEMALE" | "OTHER" | null;
  age: number | null;
  profileImage: string | null;
  username: string | null;
  bio: string | null;
  timezone: string | null;
  isActive: boolean;
  isEmailVerified: boolean;
  accountStatus: string;
  isProfileCompleted: boolean;
  niches: Niche[];
  socialLinks: SocialLink[];
}

export interface AdminAffiliatesResponse {
  affiliates: AdminAffiliate[];
  total: number;
}

// Types for Affiliate Search
export interface AffiliateEnrollment {
  programId: string;
  brandName: string;
  code: string;
  status: "APPROVED" | "PENDING" | "REJECTED";
  totalSales: number;
  totalCommissions: number;
  totalOrders: number;
  joinedAt: string;
}

export interface SearchAffiliate {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  username: string | null;
  profileImage: string | null;
  joinedAt: string;
  totalEarnings: number;
  enrollments: AffiliateEnrollment[];
  socialLinks: SocialLink[];
  isActive: boolean;
  isEmailVerified: boolean;
  accountStatus: string;
  isProfileCompleted: boolean;
}

export interface SearchAffiliatesResponse {
  data: SearchAffiliate[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Search parameters interface
export interface AffiliateSearchParams {
  page?: number;
  limit?: number;
  searchBy?: "NAME" | "EMAIL" | "USERNAME";
  keyword?: string;
  minEarnings?: number;
  maxEarnings?: number;
  joinedAfter?: string; // ISO date string
  joinedBefore?: string; // ISO date string
  sortBy?: "EARNINGS" | "NAME" | "JOIN_DATE";
  sortOrder?: "ASC" | "DESC";
  status?: "ACTIVE" | "INACTIVE";
  emailVerified?: boolean;
  hasEnrollments?: boolean;
  programIds?: string[];
}

// Service class
export class AdminAffiliatesService {
  // This method was removed in favor of using searchAffiliates for all cases

  // Search affiliates with advanced filtering and pagination
  async searchAffiliates(
    params: AffiliateSearchParams = {}
  ): Promise<SearchAffiliatesResponse> {
    try {
      const queryParams = new URLSearchParams();

      // Add pagination parameters
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());

      // Add search parameters
      if (params.searchBy) queryParams.append("searchBy", params.searchBy);
      if (params.keyword) queryParams.append("keyword", params.keyword);

      // Add filter parameters
      if (params.minEarnings !== undefined)
        queryParams.append("minEarnings", params.minEarnings.toString());
      if (params.maxEarnings !== undefined)
        queryParams.append("maxEarnings", params.maxEarnings.toString());
      if (params.joinedAfter)
        queryParams.append("joinedAfter", params.joinedAfter);
      if (params.joinedBefore)
        queryParams.append("joinedBefore", params.joinedBefore);
      if (params.status) queryParams.append("status", params.status);
      if (params.emailVerified !== undefined)
        queryParams.append("emailVerified", params.emailVerified.toString());
      if (params.hasEnrollments !== undefined)
        queryParams.append("hasEnrollments", params.hasEnrollments.toString());
      if (params.programIds && params.programIds.length > 0)
        queryParams.append("programIds", params.programIds.join(","));

      // Add sorting parameters
      if (params.sortBy) queryParams.append("sortBy", params.sortBy);
      if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

      const finalQueryString = queryParams.toString();

      const response = await adminApi.get(
        `/affiliates/admin/search?${finalQueryString}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search affiliates"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Get affiliate by ID (useful for detailed view)
  async getAffiliateById(id: string): Promise<AdminAffiliate> {
    try {
      const response = await adminApi.get(`/affiliates/find-one/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch affiliate details"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Soft-remove an affiliate from a specific program (also deletes Shopify discount code)
  async removeEnrollment(programId: string, affiliateId: string): Promise<void> {
    try {
      await adminApi.delete(`/program/${programId}/affiliates/${affiliateId}`);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to remove affiliate from program"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Update affiliate status
  async updateAffiliateStatus(id: string, isActive: boolean): Promise<void> {
    try {
      await adminApi.patch(`/affiliates/${id}/status`, { isActive });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update affiliate status"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Helper method to format affiliate data for display
  formatAffiliateForDisplay(affiliate: AdminAffiliate | SearchAffiliate) {
    const fullName = `${affiliate.firstName} ${affiliate.lastName}`;
    const displayEmail = affiliate.email;

    if ("totalEarnings" in affiliate) {
      // SearchAffiliate
      return {
        id: affiliate.id,
        name: fullName,
        email: displayEmail,
        username: affiliate.username || "Not set",
        profileImage: affiliate.profileImage,
        joinedAt: new Date(affiliate.joinedAt).toLocaleDateString(),
        totalEarnings: affiliate.totalEarnings,
        enrollmentsCount: affiliate.enrollments.length,
        hasActiveEnrollments: affiliate.enrollments.some(
          (e) => e.status === "APPROVED"
        ),
      };
    } else {
      // AdminAffiliate
      return {
        id: affiliate.id,
        name: fullName,
        email: displayEmail,
        username: affiliate.username || "Not set",
        profileImage: affiliate.profileImage,
        country: affiliate.country || "Not specified",
        age: affiliate.age || "Not specified",
        gender: affiliate.gender || "Not specified",
        isActive: affiliate.isActive,
        isEmailVerified: affiliate.isEmailVerified,
        nichesCount: affiliate.niches.length,
        socialLinksCount: affiliate.socialLinks.length,
      };
    }
  }

  // Helper method to get affiliate statistics using new admin overview endpoint
  async getAffiliateStats(): Promise<{
    totalAffiliates: number;
    totalEarnings: number;
    averageEarnings: number;
    activeEarners: number;
  }> {
    try {
      const response = await adminApi.get("/affiliates/admin/overview");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch affiliate stats:", error);
      return {
        totalAffiliates: 0,
        totalEarnings: 0,
        averageEarnings: 0,
        activeEarners: 0,
      };
    }
  }
}

export const adminAffiliatesService = new AdminAffiliatesService();
